import { FC, useCallback, useState } from "react";
import { Combobox, ComboboxOption, Label } from "@altimateai/lego";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { getUserList } from "@/helpers/apis";
import { useDebounce } from "@/helpers/hooks";
import { getTenant } from "@/helpers/tenant";
import { BaseUserSchema } from "@/types";

type BaseUser = z.infer<typeof BaseUserSchema>;

export const getUserDisplayName = (user: BaseUser) => {
  if (user.first_name || user.last_name) {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }
  return user.email;
};

interface ShareUserComboboxProps {
  selectedUserIds: string[];
  onChange: (userIds: string[]) => void;
}

/**
 * Multi-select user picker backed by the lego Combobox. Owns the user
 * search query; results require at least 2 typed characters so the
 * full tenant user list is never fetched unsolicited.
 */
const ShareUserCombobox: FC<ShareUserComboboxProps> = ({ selectedUserIds, onChange }) => {
  const tenant = getTenant();
  const [search, setSearch] = useState("");
  // The Combobox reports every keystroke; debounce so we don't fire a
  // user-search request per character typed.
  const debouncedSearch = useDebounce(search, 300);

  const { data } = useQuery({
    queryKey: ["users-search", tenant, debouncedSearch],
    queryFn: () =>
      getUserList({
        company: tenant,
        search_term: debouncedSearch,
        allUsers: true,
        include_deleted: false,
        pageSize: 20,
      }),
    enabled: debouncedSearch.length >= 2,
    staleTime: 30000,
  });

  const options: ComboboxOption[] = (data?.users ?? [])
    .filter(user => !!user.is_active)
    .map(user => ({
      value: String(user.id),
      label: getUserDisplayName(user),
      labelNode: (
        <div>
          <p className="al-text-sm al-font-medium">{getUserDisplayName(user)}</p>
          <p className="al-text-xs al-text-muted-foreground">{user.email}</p>
        </div>
      ),
    }));

  // The Combobox drives search through onLoadMore; updating local state
  // re-runs the query above with the new term.
  const handleLoadMore = useCallback(async (searchValue?: string) => {
    setSearch(searchValue ?? "");
  }, []);

  return (
    <div className="al-space-y-3">
      <Label>Add people</Label>
      <Combobox
        multiSelect
        options={options}
        value={selectedUserIds}
        onChange={onChange}
        placeholder="Select people..."
        searchPlaceholder="Search by name or email..."
        onLoadMore={handleLoadMore}
        hasMore={false}
        buttonProps={{ className: "al-w-full", "aria-label": "Select people" }}
      />
    </div>
  );
};

export default ShareUserCombobox;
